import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    CalendarIcon,
    Team25LogoEU,
    MapDropIcon,
    TeamworkCollectionsLockup
} from "./illustrations";

export type HeroTakeoverTeam25Props = {
    urlPath?: string;
};
export const HeroTakeoverTeam25 = () => {
    const intl = useIntl();

    const heroTakeoverTeam25Body = intl.formatMessage({
        id: "HomePage.hero-takeover-team-25-eu.body",
        defaultMessage:
            "Our flagship event is back! Explore cutting-edge ideas and tech, feature launches, and AI innovations that will change the game for your team."
    });

    const heroTakeoverTeam25DateInfo = intl.formatMessage({
        id: "HomePage.hero-takeover-team-25-eu.date-info",
        defaultMessage: "7-9 October"
    });

    const heroTakeoverTeam25LocationInfo = intl.formatMessage({
        id: "HomePage.hero-takeover-team-25-eu.location-info",
        defaultMessage: "Barcelona and online"
    });

    // @ts-ignore
    const heroTakeoverTeam25CTASecondaryText = intl.formatMessage({
        id: "HomePage.hero-takeover-team-25-eu.cta-secondary-text",
        defaultMessage: "See agenda"
    });

    if (checkFeatureFlagGate(featureFlags.gwp5421CollectionsHero))
        return (
            <UFOSegment name="homepage-v2-hero-collections">
                <div css={heroCollectionsCSS}>
                    <div css={containingWidthCollections}>
                        <div
                            className="left-block-text"
                            css={collectionsLeftBlock}
                        >
                            <span css={lozengeStyle}>
                                {intl.formatMessage({
                                    id: "HomePage.hero-takeover-collections.lozenge",
                                    defaultMessage: "Announcement"
                                })}
                            </span>
                            <TeamworkCollectionsLockup />
                            <p>
                                {intl.formatMessage({
                                    id: "HomePage.hero-takeover-collections.bodytext",
                                    defaultMessage:
                                        "Atlassian’s most powerful apps: Jira, Confluence, Loom and AI Agents, packaged together to drive your team to success."
                                })}
                            </p>

                            <Link
                                text={intl.formatMessage({
                                    id: "HomePage.hero-takeover-collections.cta",
                                    defaultMessage:
                                        "Learn about Teamwork Collection"
                                })}
                                href="/collections/teamwork"
                                type="primary"
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    // @ts-ignore
                                    "data-event-container":
                                        "heroTakeoverCollections",
                                    "data-product-key": "home",
                                    "data-label-english":
                                        "Learn about Teamwork Collection"
                                }}
                            />
                        </div>

                        <div className="right-block-img">
                            <Picture
                                style={{
                                    aspectRatio: "500/373",
                                    maxWidth: "100%",
                                    height: "auto"
                                }}
                                image={{
                                    width: 500,
                                    alt: "",
                                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/31g1oaixkl0abw4ci07c0e467s6o0wy7.webp"
                                }}
                                forceEagerLoad={true}
                            />
                        </div>
                    </div>
                </div>
            </UFOSegment>
        );
    if (checkFeatureFlagGate(featureFlags.gwp7711Team25BannerPhase3)) {
        return (
            <UFOSegment name="homepage-v2-hero-team-25-eu">
                <div css={heroTeamCSS}>
                    <div css={containingWidth}>
                        <div css={[shapeTransparent, leftPolygon]}>
                            <div
                                css={[
                                    shapeRightPolygonInner,
                                    shapeYellow,
                                    rightPolygon
                                ]}
                            />
                        </div>
                        <div css={[shapeBlue, leftPolygon]}>
                            <div css={[shapeNavy, rightPolygon, navyDesktop]} />
                        </div>
                        <div css={leftHero}>
                            <div css={[shapeNavy, rightPolygon, navyMobile]} />

                            <Team25LogoEU />
                            <div css={timePlaceDetails}>
                                <div>
                                    <CalendarIcon />
                                    {heroTakeoverTeam25DateInfo}
                                </div>
                                <div>
                                    <MapDropIcon />
                                    {heroTakeoverTeam25LocationInfo}
                                </div>
                            </div>
                            <h2 className="hero hero-25">
                                <FormattedMessage
                                    id="HomePage.hero-takeover-team-25-eu.heading"
                                    defaultMessage="The conference for <span>AI-powered teamwork</span>"
                                    values={DEFAULT_VALUES}
                                />
                            </h2>
                            <p>{heroTakeoverTeam25Body}</p>

                            <div css={buttonArea}>
                                <Adg4Button
                                    variant="primary"
                                    color="yellow"
                                    href="https://events.atlassian.com/teameurope?utm_medium=event-web-referral&utm_source=cross-promo&event_context=701qb00000pbqkqyar&utm_campaign=K:event|H:in-person|N:team_europe_25|P:agnostic|O:event-marketing|F:pre-event|C:event-home&utm_content=takeover_screen_9.1&event_id=243301"
                                    dataEventProps={{
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        // @ts-ignore
                                        "data-event-container":
                                            "heroTakeoverteam25EU",
                                        "data-product-key": "home",
                                        "data-label-english": "Register now"
                                    }}
                                >
                                    <FormattedMessage
                                        id="HomePage.hero-takeover-team-25-eu.cta-primary-text"
                                        defaultMessage="Register now"
                                    />
                                </Adg4Button>

                                {/* removing agenda link until we get a URL
                                    <Link
                                        text={heroTakeoverTeam25CTASecondaryText}
                                        href="https://events.atlassian.com/teameurope?utm_medium=event-web-referral&utm_source=cross-promo&event_context=701qb00000pbqkqyar&utm_campaign=K:event|H:in-person|N:team_europe_25|P:agnostic|O:event-marketing|F:pre-event|C:event-home&utm_content=takeover_screen_9.1&event_id=243301"
                                        type="primary"
                                        bgColor="bgBlackBlue"
                                        dataEventProps={{
                                            "data-event": "clicked",
                                            "data-event-component": "linkButton",
                                            "data-event-container":
                                                "heroTakeoverteam2EU",
                                            "data-product-key": "home",
                                            "data-label-english": "See agenda"
                                        }}
                                    />
                                */}
                            </div>
                        </div>
                        <div css={rightHero}>
                            <video
                                playsInline={true}
                                muted={true}
                                preload="auto"
                                loop={true}
                                css={css({
                                    border: 0,
                                    maxWidth: "100%",
                                    width: "550px",
                                    height: "310px",
                                    aspectRatio: "550/310"
                                })}
                                poster="https://dam-cdn.atl.orangelogic.com/AssetLink/03aa4b48x1751y87o646838152ssggwi.webp"
                                autoPlay={true}
                            >
                                <source
                                    src="https://dam-cdn.atl.orangelogic.com/AssetLink/b7i5j7lls175m5n4h7mx7rl2w12g8t40.mp4"
                                    type="video/mp4"
                                />
                            </video>
                        </div>
                    </div>
                </div>
            </UFOSegment>
        );
    }
    return <></>;
};

const heroCollectionsCSS = css({
    background: wacToken("color.extended.blue100"),
    marginBlockEnd: token("space.300")
});

const containingWidthCollections = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "56px",
    width: "1200px",
    maxWidth: "100%",
    justifyContent: "space-between",
    padding: `0 ${token("space.200")}`,
    margin: "auto",
    [media.below.md]: {
        padding: `0 60px`
    },
    [media.below.sm]: {
        flexDirection: "column",
        textAlign: "center",
        alignItems: "center",
        gap: token("space.200"),
        justifyContent: "center"
    }
});

const collectionsLeftBlock = css({
    display: "flex",
    flexDirection: "column",
    width: "500px",
    maxWidth: "100%",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: `${token("space.500")} 0`,
    [media.below.sm]: {
        textAlign: "center",
        padding: `${token("space.600")} ${token("space.600")} 0`,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.link-wrapper`]: {
            margin: "auto"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`> svg`]: {
        width: "400px",
        height: "auto",
        aspectRatio: "788/191"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        fontSize: "1.25rem"
    },
    [media.below.md]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        p: {
            fontSize: "1.125rem"
        }
    }
});

const lozengeStyle = css({
    background: wacToken("color.extended.blue200"),
    color: wacToken("color.secondary.navy"),
    textTransform: "uppercase",
    fontSize: "0.6875rem",
    lineHeight: 1,
    letterSpacing: "0.025rem",
    fontWeight: 500,
    padding: `${token("space.050")} ${token("space.100")}`,
    borderRadius: wacToken("corner.radius.pill")
});

const heroTeamCSS = css({
    overflow: "hidden",
    background: wacToken("color.extended.blue200"),
    margin: `0 0 ${token("space.600")}`,
    [media.below.md]: {
        background: wacToken("color.neutral.light0"),
        margin: `0 0 ${token("space.200")}`
    }
});

const containingWidth = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "76px",
    width: "1200px",
    maxWidth: "100%",
    padding: `${token("space.800")} ${token("space.300")}`,
    margin: "auto",
    [media.below.md]: {
        flexDirection: "column",
        textAlign: "center",
        alignItems: "center",
        gap: token("space.200"),
        justifyContent: "center",
        padding: `${token("space.800")} 0 0`
    }
});

const leftHero = css({
    "position": "relative",
    "display": "flex",
    "gap": token("space.300"),
    "flexDirection": "column",
    "alignItems": "flex-start",
    "width": "48%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2.hero": {
        "fontWeight": "400",
        "fontSize": "2.75rem",
        "margin": 0,
        "lineHeight": "1.2",
        "color": wacToken("color.neutral.light0"),
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "strong, span": {
            display: "block"
        },

        "&.hero-25": {
            fontWeight: "600",
            [media.below.lg]: {
                fontSize: "2rem"
            }
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        color: wacToken("color.neutral.light0"),
        fontSize: "0.875rem",
        margin: 0
    },
    [media.below.md]: {
        width: "auto",
        maxWidth: "500px",
        padding: `0 ${token("space.300")}`,
        textAlign: "center",
        alignItems: "center",
        justifyContent: "center"
    }
});

const rightHero = css({
    position: "relative",
    padding: `${token("space.400")} 0`,
    width: "52%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "100%",
        height: "auto",
        aspectRatio: "555/313"
    },
    [media.below.md]: {
        width: "100%"
    }
});

const leftPolygon = css({
    position: "absolute",
    transformOrigin: "100% 0",
    transform: "rotate(3.83deg)",
    width: "1523px",
    height: "559px",
    insetBlockStart: "70px",
    insetInlineEnd: "-50px",
    [media.below.lg]: {
        width: "135%"
    },
    [media.below.md]: {
        transform: "none",
        insetBlockStart: "-10px",
        backgroundImage: `linear-gradient(to bottom, ${wacToken(
            "color.extended.blue700"
        )} calc(100% - 90px), transparent calc(100% - 90px))`
    }
});

const rightPolygon = css({
    position: "absolute",
    width: "1251px",
    height: "527px",
    insetInlineStart: "54%",
    insetBlockStart: "-50px",
    transform: "rotate(-8deg)",
    transformOrigin: "0 0"
});

const shapeTransparent = css({
    [media.below.md]: {
        height: "100%"
    }
});

const shapeBlue = css({
    overflow: "hidden",
    background: wacToken("color.extended.blue700"),
    [media.below.md]: {
        overflow: "visible",
        height: "612px"
    }
});

const shapeRightPolygonInner = css({
    transform: "rotate(-3.72deg)",
    [media.below.md]: {
        height: "200px",
        insetInlineStart: "22%",
        transform: "rotate(-2deg)",
        insetBlockEnd: "0",
        insetBlockStart: "auto",
        transformOrigin: "0 100%"
    }
});

const shapeYellow = css({
    background: wacToken("color.extended.purple400")
});
const shapeNavy = css({
    background: wacToken("color.extended.blue900"),
    transform: "rotate(-8.72deg)",
    [media.below.md]: {
        display: "block",
        insetBlockStart: "auto",
        insetBlockEnd: "-120px",
        height: "50px",
        insetInlineStart: "-30%",
        insetInlineEnd: "auto",
        width: "900px",
        transform: "rotate(1deg)"
    }
});

const navyDesktop = css({
    display: "block",
    [media.below.md]: {
        display: "none"
    }
});

const navyMobile = css({
    display: "none",
    insetBlockStart: "111%",
    [media.below.md]: {
        display: "block"
    }
});

const timePlaceDetails = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.300"),
    fontSize: "1rem",
    [media.below.md]: {
        fontSize: "0.8rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    div: {
        color: wacToken("color.neutral.light0"),
        alignItems: "center",
        display: "flex",
        gap: token("space.100")
    }
});

const buttonArea = css({
    "display": "flex",
    "flexWrap": "wrap",
    "gap": token("space.300"),
    "alignItems": "center",
    "justifyContent": "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".link-wrapper a:visited": {
        "color": wacToken("color.neutral.light0"),
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "svg path": {
            fill: wacToken("color.neutral.light0")
        },
        ":hover": {
            "color": wacToken("color.extended.blue200"),
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "svg path": {
                fill: wacToken("color.extended.blue200")
            }
        }
    }
});
HeroTakeoverTeam25.displayName = "HeroTakeoverTeam25";
